"use client";

import { ReactNode } from "react";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import ScoreTimeline from "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/AnalysisOverview/ScoreTimeline/ScoreTimeline";
import TopicChart from "@/app/(components)/specifics/tools/CallAnalysis/components/Analysis/AnalysisOverview/TopicChart";
import { useFetchProjectDashboard } from "@/app/hooks/fetchs/project/useFetchProjectDashboard";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ProjectOverviewStats } from "@/app/types/project/ProjectOverview";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import { isActivationDone } from "./Dashboard.logic";
import styles from "./Dashboard.module.css";
import DashboardCounters from "./DashboardCounters";
import DashboardTasks from "./DashboardTasks";

type Props = {
  params: PageParams;
  projectId: number;
  stats?: ProjectOverviewStats;
  enabled: boolean;
  description: ReactNode;
};

export default function Dashboard(props: Props) {
  const T = useT();
  const { params, projectId, stats, enabled, description } = props;

  const { data: dashboard } = useFetchProjectDashboard(projectId, enabled);

  if (!dashboard || !stats) {
    return (
      <div className={styles.loader}>
        <PointsLoader color="var(--color-green-3)" delay={Limits.DASHBOARD_LOADER_DELAY} />
      </div>
    );
  }

  const activationDone = isActivationDone(stats);
  const showSection = (hasContent: boolean) => activationDone || hasContent;

  return (
    <div className={styles.container}>
      <DashboardCounters counters={dashboard.counters} />

      <div className={styles.grid}>
        <section className={UStyles.classNames(styles.section, styles.descriptionCard)}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{T(TKey.DASHBOARD_PROJECT_TITLE)}</h2>
          </div>

          {description}
        </section>

        <DashboardTasks params={params} tasks={dashboard.tasks} stats={stats} />

        {showSection(dashboard.calls.timeline.length > 1) && (
          <ScoreTimeline timeline={dashboard.calls.timeline} compact={true} />
        )}

        {showSection(dashboard.market.length > 0) && (
          <TopicChart
            title={T(TKey.CALL_OVERVIEW_TOP_INSIGHTS_TITLE) as string}
            topics={dashboard.market}
            total={dashboard.calls.analysedCalls}
          />
        )}
      </div>
    </div>
  );
}
